/**
 * Utility functions for working with invoice charges
 * 
 * These utilities help migrate from shipment.charges to invoice-based charges
 */

import { Invoice, ShipmentCharge, GetInvoiceForShipmentQuery, GetInvoicesForShipmentsQuery } from '../graphql/generated';

// Type aliases for invoice data from queries
type InvoiceQueryData = GetInvoiceForShipmentQuery['invoices']['data'][0];
type InvoicesQueryData = GetInvoicesForShipmentsQuery['invoices']['data'];

// Union type that accepts both full Invoice and query result types
type InvoiceLike = Invoice | InvoiceQueryData;
type InvoicesLike = Invoice[] | InvoicesQueryData;

/**
 * Get charges for a specific shipment from an invoice
 * Handles both single-shipment and consolidated invoices
 * 
 * @param invoice - The invoice containing charges
 * @param shipmentId - The shipment ID to filter charges for
 * @returns Array of charges for the shipment
 */
export function getChargesForShipment(
  invoice: InvoiceLike | null | undefined,
  shipmentId: string
): ShipmentCharge[] {
  if (!invoice) {
    return [];
  }

  // For single-shipment invoices, return all charges
  if (invoice.shipmentIds?.length === 1) {
    return invoice.charges as ShipmentCharge[];
  }

  // For consolidated invoices, filter by shipmentId
  return invoice.charges.filter(
    (charge) =>
      charge.shipmentId === shipmentId ||
      !charge.shipmentId // Include charges without shipmentId for backward compatibility
  ) as ShipmentCharge[];
}

/**
 * Find the invoice for a specific shipment from a list of invoices
 * 
 * @param invoices - Array of invoices
 * @param shipmentId - The shipment ID to find invoice for
 * @returns The invoice containing the shipment, or undefined
 */
export function findInvoiceForShipment(
  invoices: InvoicesLike | null | undefined,
  shipmentId: string
): InvoiceLike | undefined {
  if (!invoices) {
    return undefined;
  }

  return invoices.find((invoice) =>
    invoice.shipmentIds?.includes(shipmentId)
  );
}

/**
 * Calculate total charges for a shipment
 * 
 * @param invoice - The invoice containing charges
 * @param shipmentId - The shipment ID to calculate total for
 * @returns Total charge amount
 */
export function calculateShipmentChargeTotal(
  invoice: InvoiceLike | null | undefined,
  shipmentId: string
): number {
  const charges = getChargesForShipment(invoice, shipmentId);
  return charges.reduce((sum, charge) => sum + (charge.total || 0), 0);
}

/**
 * Calculate total charges for multiple shipments
 * 
 * @param invoices - Array of invoices
 * @param shipmentIds - Array of shipment IDs
 * @returns Total charge amount across all shipments
 */
export function calculateMultipleShipmentsChargeTotal(
  invoices: InvoicesLike | null | undefined,
  shipmentIds: string[]
): number {
  if (!invoices) {
    return 0;
  }

  let total = 0;
  for (const shipmentId of shipmentIds) {
    const invoice = findInvoiceForShipment(invoices, shipmentId);
    total += calculateShipmentChargeTotal(invoice, shipmentId);
  }

  return total;
}

/**
 * Get all charges from multiple invoices
 * Useful for displaying charges across multiple shipments
 * 
 * @param invoices - Array of invoices
 * @returns Flattened array of all charges
 */
export function getAllChargesFromInvoices(
  invoices: InvoicesLike | null | undefined
): ShipmentCharge[] {
  if (!invoices) {
    return [];
  }

  return invoices.flatMap((invoice) => invoice.charges) as ShipmentCharge[];
}

/**
 * Group charges by shipment ID
 * Useful for displaying charges in a consolidated invoice view
 * 
 * @param invoice - The invoice containing charges
 * @returns Map of shipmentId to charges array
 */
export function groupChargesByShipment(
  invoice: InvoiceLike | null | undefined
): Map<string, ShipmentCharge[]> {
  const grouped = new Map<string, ShipmentCharge[]>();

  if (!invoice) {
    return grouped;
  }

  for (const charge of invoice.charges) {
    const shipmentId = charge.shipmentId || 'unassigned';
    const existing = grouped.get(shipmentId) || [];
    grouped.set(shipmentId, [...existing, charge as ShipmentCharge]);
  }

  return grouped;
}

/**
 * Check if an invoice is consolidated (contains multiple shipments)
 * 
 * @param invoice - The invoice to check
 * @returns True if invoice contains multiple shipments
 */
export function isConsolidatedInvoice(
  invoice: InvoiceLike | null | undefined
): boolean {
  return (invoice?.shipmentIds?.length || 0) > 1;
}

/**
 * Get invoice status display text
 * 
 * @param invoice - The invoice
 * @returns Human-readable status text
 */
export function getInvoiceStatusText(
  invoice: InvoiceLike | null | undefined
): string {
  if (!invoice) {
    return 'No Invoice';
  }

  const statusMap: Record<string, string> = {
    NOT_INVOICED: 'Not Invoiced',
    PENDING_COMPLETION: 'Pending Completion',
    DRAFT: 'Draft',
    SENT: 'Sent',
    ON_HOLD: 'On Hold',
    CLOSED: 'Closed',
    PAID: 'Paid',
    CANCELLED: 'Cancelled',
    TRANSFERRED: 'Transferred',
  };

  return statusMap[invoice.status] || invoice.status;
}
